import { useRef, useState } from 'react';
import { Popconfirm } from 'components/ui';

type PopConfirmSettings = {
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
};

const defaultSettings: PopConfirmSettings = {
  title: 'Удалить?',
  description: 'Элемент удалится безвозвратно',
  confirmText: 'Удалить',
  cancelText: 'Отмена',
  side: 'bottom',
  align: 'end',
};

/**
 * Аналог useConfirm, но для Popconfirm (попап у кнопки, не модалка)
 * Якорь: при вызове хука нужно повесить `anchorRef` на кнопку-триггер
 */
export const usePopConfirm = () => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [settings, setSettings] = useState<PopConfirmSettings>(defaultSettings);

  const promiseRef = useRef<{
    resolve: () => void;
    reject: () => void;
  } | null>(null);

  const popConfirm = (
    confirmSettings: Partial<PopConfirmSettings> = {}
  ): Promise<void> => {
    setSettings({ ...defaultSettings, ...confirmSettings });
    setOpen(true);

    return new Promise<void>((resolve, reject) => {
      promiseRef.current = { resolve, reject };
    });
  };

  const handleConfirm = () => {
    promiseRef.current?.resolve();
    promiseRef.current = null;
    setOpen(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      promiseRef.current?.reject();
      promiseRef.current = null;
    }
    setOpen(nextOpen);
  };

  const stateRef = useRef({
    open,
    settings,
    handleConfirm,
    handleOpenChange,
    anchorRef,
  });
  stateRef.current = {
    open,
    settings,
    handleConfirm,
    handleOpenChange,
    anchorRef,
  };

  const Component = useRef(() => {
    const {
      open,
      settings,
      handleConfirm,
      handleOpenChange,
      anchorRef,
    } = stateRef.current;

    return (
      <Popconfirm
        anchorRef={anchorRef}
        side={settings.side}
        align={settings.align}
        title={settings.title}
        description={settings.description}
        confirmText={settings.confirmText}
        cancelText={settings.cancelText}
        open={open}
        onOpenChange={handleOpenChange}
        onConfirm={handleConfirm}
      />
    );
  }).current;

  return [Component, popConfirm, anchorRef, open] as const;
};
